import React, { useState, useEffect, useCallback } from 'react';
import {
  PuzzlePieceIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const AddonInventoryManager = ({ serviceId, addons, onAddonsChange, onError }) => {
  const [addonInventories, setAddonInventories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [showAdjustment, setShowAdjustment] = useState(null);

  const fetchAddonInventories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/inventory/addons/service/${serviceId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.success) {
        setAddonInventories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching addon inventories:', error);
      onError('Failed to load addon inventories');
    } finally {
      setLoading(false);
    }
  }, [serviceId, onError]);

  useEffect(() => {
    if (serviceId) {
      fetchAddonInventories();
    }
  }, [serviceId, fetchAddonInventories]);

  const createAddonInventory = async (addonData) => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const response = await axios.post('/api/inventory/addons', addonData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        await fetchAddonInventories();
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'API returned unsuccessful response');
      }
    } catch (error) {

      if (error.response) {
        // API responded with error status
        const apiError = error.response.data?.error || `API Error: ${error.response.status} ${error.response.statusText}`;
        throw new Error(apiError);
      } else if (error.request) {
        // Request was made but no response received
        throw new Error('No response from server. Please check your connection.');
      } else {
        // Something else happened
        throw new Error(error.message || 'Failed to create addon inventory');
      }
    }
  };

  const updateAddonInventory = async (id, updates) => {
    try {
      const response = await axios.put(`/api/inventory/addons/${id}`, updates, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.success) {
        await fetchAddonInventories();
        return response.data.data;
      }
    } catch (error) {
      console.error('Error updating addon inventory:', error);
      throw new Error('Failed to update addon inventory');
    }
  };

  const adjustInventory = async (addonId, newQuantity, reason) => {
    try {
      const response = await axios.put(`/api/inventory/addons/${addonId}/adjust`, {
        newQuantity,
        reason
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.success) {
        await fetchAddonInventories();
        setShowAdjustment(null);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to adjust inventory');
      }
    } catch (error) {
      console.error('Error adjusting inventory:', error);
      throw error;
    }
  };

  const handleAddonChange = (index, field, value) => {
    const updatedAddons = [...addons];
    updatedAddons[index] = { ...updatedAddons[index], [field]: value };
    onAddonsChange(updatedAddons);
  };

  const addNewAddon = () => {
    const newAddon = {
      id: `temp_${Date.now()}`,
      name: '',
      rate: '',
      rateType: 'FIXED',
      trackInventory: false,
      totalQuantity: 10,
      maxPerBooking: null,
      lowStockThreshold: null
    };
    onAddonsChange([...addons, newAddon]);
  };

  const removeAddon = (index) => {
    const updatedAddons = addons.filter((_, i) => i !== index);
    onAddonsChange(updatedAddons);
  };

  const createInventoryForAddon = async (addon, index) => {
    if (!serviceId) {
      onError('Service must be created before setting up inventory');
      return;
    }

    if (!addon.name || addon.name.trim() === '') {
      onError('Addon name is required before setting up inventory tracking');
      return;
    }

    try {
      // Always use addon.id if it exists, otherwise generate a consistent ID
      const addonId = addon.id ? String(addon.id) : `${serviceId}_${index}`;
      const inventoryData = {
        addonId,
        serviceId,
        addonName: addon.name.trim(),
        totalQuantity: parseInt(addon.totalQuantity) || 10,
        maxPerBooking: addon.maxPerBooking ? parseInt(addon.maxPerBooking) : null,
        trackInventory: true,
        lowStockThreshold: addon.lowStockThreshold ? parseInt(addon.lowStockThreshold) : null
      };

      await createAddonInventory(inventoryData);

      // Force a refresh of inventories and wait for it
      await fetchAddonInventories();

      // Check if we can now find the inventory
      const foundInventory = getInventoryForAddon(addonId);

      // Update the addon with tracking enabled
      handleAddonChange(index, 'hasInventory', true);
      handleAddonChange(index, 'trackInventory', true);

      // Force component re-render by updating a dummy state
      setLoading(true);
      setTimeout(() => setLoading(false), 100);

    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to setup inventory tracking';
      onError(errorMessage);
    }
  };

  const getInventoryForAddon = (addonId) => {
    const found = addonInventories.find(inv => inv.addonId === addonId);
    return found;
  };

  const InventoryAdjustmentModal = ({ addon, inventory, onClose }) => {
    const [newQuantity, setNewQuantity] = useState(inventory.totalQuantity);
    const [reason, setReason] = useState('');
    const [adjusting, setAdjusting] = useState(false);

    const handleAdjust = async () => {
      try {
        setAdjusting(true);
        await adjustInventory(inventory.addonId, newQuantity, reason || 'Manual adjustment');
        onClose();
      } catch (error) {
        onError(error.message);
      } finally {
        setAdjusting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Adjust Inventory: {addon.name}
          </h3>

          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600">Current Status:</div>
              <div className="text-lg font-medium">
                Total: {inventory.totalQuantity} |
                Reserved: {inventory.reservedQuantity} |
                Available: {inventory.totalQuantity - inventory.reservedQuantity}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Total Quantity
              </label>
              <input
                type="number"
                value={newQuantity}
                onChange={(e) => setNewQuantity(parseInt(e.target.value))}
                min={inventory.reservedQuantity}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              />
              {newQuantity < inventory.reservedQuantity && (
                <div className="mt-2 text-red-600 text-sm">
                  Cannot set below {inventory.reservedQuantity} (currently reserved)
                </div>
              )}
            </div>


            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAdjust}
                disabled={adjusting || newQuantity < inventory.reservedQuantity}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300"
              >
                {adjusting ? 'Adjusting...' : 'Confirm Adjustment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Add-ons List */}
      <div className="space-y-4">
        {addons.map((addon, index) => {
          // Use the same consistent addonId logic as creation
          const addonIdForLookup = addon.id ? String(addon.id) : `${serviceId}_${index}`;
          const inventory = getInventoryForAddon(addonIdForLookup);
          const isPerItem = addon.rateType === 'PER_ITEM';

          return (
            <div key={addon.id || index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Add-on name (e.g., Tennis racquet, Golf cart)"
                    value={addon.name || ''}
                    onChange={(e) => handleAddonChange(index, 'name', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm font-medium"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeAddon(index)}
                  className="ml-3 text-red-600 hover:text-red-800"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={addon.rate || ''}
                    onChange={(e) => handleAddonChange(index, 'rate', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rate Type</label>
                  <select
                    value={addon.rateType || 'FIXED'}
                    onChange={(e) => handleAddonChange(index, 'rateType', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 text-sm"
                  >
                    <option value="FIXED">Fixed Price</option>
                    <option value="PER_PERSON">Per Person</option>
                    <option value="PER_ITEM">Per Item</option>
                  </select>
                </div>
              </div>

              {/* Inventory Management for PER_ITEM */}
              {isPerItem && (
                <div className="border-t border-gray-200 pt-4">
                  {!inventory ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <CubeIcon className="h-5 w-5 text-blue-500 mr-2" />
                        <h4 className="text-sm font-medium text-blue-900">Inventory Management</h4>
                      </div>
                      <p className="text-sm text-blue-700 mb-4">
                        Per-item add-ons can have inventory tracking to prevent overbooking.
                      </p>

                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Total Quantity</label>
                          <input
                            type="number"
                            min="1"
                            value={addon.totalQuantity || 10}
                            onChange={(e) => handleAddonChange(index, 'totalQuantity', e.target.value)}
                            className="block w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Max per Booking</label>
                          <input
                            type="number"
                            min="1"
                            placeholder="No limit"
                            value={addon.maxPerBooking || ''}
                            onChange={(e) => handleAddonChange(index, 'maxPerBooking', e.target.value)}
                            className="block w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Low Stock Alert</label>
                          <input
                            type="number"
                            min="0"
                            placeholder="No alerts"
                            value={addon.lowStockThreshold || ''}
                            onChange={(e) => handleAddonChange(index, 'lowStockThreshold', e.target.value)}
                            className="block w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => createInventoryForAddon(addon, index)}
                        disabled={!serviceId}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 text-sm"
                      >
                        {serviceId ? 'Setup Inventory Tracking' : 'Save service first to setup inventory'}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                          <h4 className="text-sm font-medium text-green-900">Inventory Tracking Active</h4>
                        </div>
                        <button
                          onClick={() => setShowAdjustment(inventory)}
                          className="text-purple-600 hover:text-purple-800 text-sm"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Total</div>
                          <div className="font-medium">{inventory.totalQuantity}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Reserved</div>
                          <div className="font-medium">{inventory.reservedQuantity}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Available</div>
                          <div className={`font-medium ${inventory.isLowStock ? 'text-red-600' : 'text-green-600'}`}>
                            {inventory.availableQuantity}
                            {inventory.isLowStock && <ExclamationTriangleIcon className="h-4 w-4 inline ml-1" />}
                          </div>
                        </div>
                      </div>

                      {inventory.maxPerBooking && (
                        <div className="mt-2 text-xs text-gray-600">
                          Max per booking: {inventory.maxPerBooking}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {(!addons || addons.length === 0) && (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <PuzzlePieceIcon className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No add-ons configured</p>
            <p className="text-xs text-gray-400">Click "Add New Add-on" to create your first add-on</p>
          </div>
        )}

        {/* Add New Add-on Button */}
        <div className="mt-4">
          <button
            type="button"
            onClick={addNewAddon}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add New Add-on
          </button>
        </div>
      </div>

      {/* Inventory Adjustment Modal */}
      {showAdjustment && (
        <InventoryAdjustmentModal
          addon={addons.find(a => a.id === showAdjustment.addonId)}
          inventory={showAdjustment}
          onClose={() => setShowAdjustment(null)}
        />
      )}
    </div>
  );
};

export default AddonInventoryManager;